import { apiSlice } from '../apiSlice';
import {
	NotificationDTO,
	NotificationListFilter,
	NotificationReadStateRequest,
} from '../DTO/notification/notification.interface';
import { Paginated, SuccessResponse } from '../DTO/common.interface';

/**
 * Notification panel endpoints (DMS-REQ-WEB-NOT-001).
 *
 * Base path is `/web/notification`, singular.
 *
 * The bell's own two calls disable the full-screen loader — the badge count
 * refreshes on every page load and the preview loads behind a popover, so a blocking
 * overlay there would fire at moments the staff member did not ask for anything.
 * Everything else behaves like the rest of the portal.
 */
export const notificationApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * Notifications addressed to the logged-in staff member, newest first, for
		 * the list page.
		 *
		 * The recipient and the branch are taken from the bearer token — the ids in
		 * the body are ignored by the backend, so one staff member cannot page
		 * another's inbox. `pagination` must be sent; omitting it is a 500 rather
		 * than a 400.
		 */
		getNotificationList: builder.query<
			SuccessResponse<Paginated<NotificationDTO>>,
			NotificationListFilter
		>({
			query: (body) => ({
				url: `/web/notification/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['NotificationList'],
		}),
		/**
		 * The same page of notifications for the bell popover.
		 *
		 * A separate endpoint only so it can opt out of the full-screen loader:
		 * `extraOptions` is fixed per endpoint, and an overlay over the whole app
		 * while a popover fills itself in would be worse than the popover being
		 * briefly empty. It carries the same tag, so read-state changes refresh both.
		 */
		getNotificationPreview: builder.query<
			SuccessResponse<Paginated<NotificationDTO>>,
			NotificationListFilter
		>({
			query: (body) => ({
				url: `/web/notification/page`,
				method: 'POST',
				body,
			}),
			extraOptions: { disableFullScreenLoader: true },
			providesTags: ['NotificationList'],
		}),
		/**
		 * Full text of one notification, for the detail page.
		 *
		 * Does not mark it read — the detail page fires that itself. An unknown or
		 * malformed id comes back as 400, not 404, which is the "no longer
		 * available" state.
		 */
		getNotificationDetail: builder.query<
			SuccessResponse<NotificationDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/notification/${id}`,
				method: 'GET',
			}),
			providesTags: ['NotificationDetail'],
		}),
		/** Bell badge. `data` is a plain integer, `0` when nothing is unread. */
		getNotificationUnreadCount: builder.query<SuccessResponse<number>, void>({
			query: () => ({
				url: `/web/notification/query-unread-count`,
				method: 'GET',
			}),
			extraOptions: { disableFullScreenLoader: true },
			providesTags: ['NotificationUnreadCount'],
		}),
		/**
		 * Mark notifications read for the logged-in staff member.
		 *
		 * Read state is per recipient — marking one read must not affect the other
		 * staff members the same notification went to.
		 *
		 * Already-read ids are harmless, so callers can pass a whole selection
		 * without filtering it down to the unread ones first. The batch is applied
		 * one id at a time and is not transactional: on error some ids may already
		 * have changed, so the invalidated tags re-fetch rather than patch locally.
		 */
		batchReadNotifications: builder.mutation<
			SuccessResponse<null>,
			NotificationReadStateRequest
		>({
			query: (body) => ({
				url: `/web/notification/batch-read`,
				method: 'PUT',
				body,
			}),
			// The detail page marks a notification read as it opens, so invalidating
			// its own query would refetch it right after it loaded. Nothing on that
			// page shows the read state, and the list and the badge both refresh.
			invalidatesTags: ['NotificationUnreadCount', 'NotificationList'],
		}),
		/** Put notifications back to unread; same batch semantics as above. */
		batchUnreadNotifications: builder.mutation<
			SuccessResponse<null>,
			NotificationReadStateRequest
		>({
			query: (body) => ({
				url: `/web/notification/batch-unread`,
				method: 'PUT',
				body,
			}),
			// The detail page marks a notification read as it opens, so invalidating
			// its own query would refetch it right after it loaded. Nothing on that
			// page shows the read state, and the list and the badge both refresh.
			invalidatesTags: ['NotificationUnreadCount', 'NotificationList'],
		}),
	}),
});

export const {
	useGetNotificationListQuery,
	useGetNotificationPreviewQuery,
	useGetNotificationDetailQuery,
	useGetNotificationUnreadCountQuery,
	useBatchReadNotificationsMutation,
	useBatchUnreadNotificationsMutation,
} = notificationApiSlice;
